import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {
	TaskSingleVoyageRowForTable,
	TaskSingleVoyageTransformForTable,
	TaskWayPointSingleVoyage
} from "@containers/singleVoyage/tasks/table/types";
import {getKeyTransformData} from "@helpers/transform/transform";
import moment from 'moment'
import {DATE_FORMAT_TASKS_SINGLE_VOYAGE} from "@containers/singleVoyage/tasks/table/constants/dateFormat";
import {getLabelStatusTaskSingleVoyage} from "@containers/singleVoyage/tasks/helpers/getLabelStatus";
import {
	useGetTaskConfigByIdSingleVoyage
} from "@containers/singleVoyage/taskTemplate/hooks/useGetTaskConfigByIdSingleVoyage";

const planCompletionTimeTransformKey = getKeyTransformData('planCompletionTime')
const statusTransformKey = getKeyTransformData('status')
const waypointsTransformKey = getKeyTransformData('waypoints')
const taskConfigTemplateIdTransformKey = getKeyTransformData('taskConfigTemplateId')

export function useTransformTableTasksSingleVoyage({
													   tasks,
													   waypoints,
													   targetWaypointIds,
													   accountId,
												   }: {
	tasks: TaskSingleVoyageRowForTable[],
	waypoints: TaskWayPointSingleVoyage[]
	targetWaypointIds: string[]
	accountId: string | number
}): {
	transformData: TaskSingleVoyageTransformForTable[]
	isFetchingTransform: boolean
	isLoadingTransform: boolean
} {
	
	const {t} = useTranslation()
	const {
		getTaskConfigById,
		isFetching,
		isLoading,
	} = useGetTaskConfigByIdSingleVoyage({
		accountId,
	})
	
	const transformData: TaskSingleVoyageTransformForTable[] = useMemo(() => {
		if (!tasks?.length) {
			return []
		}
		
		const arr: TaskSingleVoyageTransformForTable[] = tasks.map((task) => {
			const currentWaypoints = waypoints?.filter((point) => task?.waypointIds?.includes(Number(point?.id)))
			const waypointNames = currentWaypoints?.map((point) => point.name)?.join('\n,')
			const taskTemplateConfig = getTaskConfigById(task.taskConfigTemplateId)
			return {
				...task,
				waypoints: currentWaypoints,
				id: task.id || '' as any,
				[waypointsTransformKey]: waypointNames,
				[planCompletionTimeTransformKey]: task.planCompletionTime ? moment(task.planCompletionTime).format(DATE_FORMAT_TASKS_SINGLE_VOYAGE) : '----',
				[statusTransformKey]: t(getLabelStatusTaskSingleVoyage(task.status)),
				[taskConfigTemplateIdTransformKey]: taskTemplateConfig?.name || task.taskConfigTemplateId,
				subtasks: task.subtasks.map((subtask) => {
					const taskTemplateConfig = getTaskConfigById(subtask.taskConfigTemplateId)
					const currentWaypoints = waypoints?.filter((point) => subtask?.waypointIds?.includes(Number(point?.id)))
					return {
						...subtask,
						waypoints: currentWaypoints,
						[planCompletionTimeTransformKey]: subtask.planCompletionTime ? moment(subtask.planCompletionTime).format(DATE_FORMAT_TASKS_SINGLE_VOYAGE) : '----',
						[statusTransformKey]: t(getLabelStatusTaskSingleVoyage(subtask.status)),
						[taskConfigTemplateIdTransformKey]: taskTemplateConfig?.name || subtask.taskConfigTemplateId,
					}
				})
			}
		})
		if (!targetWaypointIds?.length) {
			return arr;
		}
		return arr?.filter((task) => {
			return task?.waypointIds?.some((id) => targetWaypointIds.includes(id?.toString()))
		})
		
	}, [
		tasks,
		waypoints,
		targetWaypointIds,
		getTaskConfigById
	])
	
	return {
		transformData,
		isFetchingTransform: isFetching,
		isLoadingTransform: isLoading,
	}
}
